import React from "react";
import {
  AbsoluteFill,
  Img,
  OffthreadVideo,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { loadFont } from "@remotion/google-fonts/Roboto";
import { COLOR, LOGO } from "../../../brand/theme";

// Текстовый/субтитровый шрифт бренда — Roboto (official fallback, лучший для длинного текста).
const { fontFamily } = loadFont("normal", { weights: ["400", "500", "700", "900"] });

// Акцент на синем фоне — яркий жёлтый (Andrey: золото плохо, нужен жёлтый).
const GOLD = "#FFD400";

export type ShortReelWord = { text: string; start: number; end: number };
export type ShortReelProps = {
  clipSrc: string; // staticFile path, напр. "clips/raw_4.mp4"
  title: string; // заголовок под видео (название момента)
  accentWord?: string; // слово заголовка золотом
  words: ShortReelWord[]; // пословный транскрипт клипа (ребейзнут к 0)
  durationSec: number; // длина клипа
};

// Длительность сцены = длина клипа.
export const calcShortReelMetadata = ({ props }: { props: ShortReelProps }) => ({
  durationInFrames: Math.max(30, Math.round((props.durationSec || 10) * 30)),
});

/** Группируем слова в реплики по ≤3 слова (запрос Andrey: титры по 2-3 слова). */
function toCues(words: ShortReelWord[]) {
  const cues: { words: ShortReelWord[]; start: number; end: number }[] = [];
  for (let i = 0; i < words.length; i += 3) {
    const chunk = words.slice(i, i + 3);
    if (chunk.length) cues.push({ words: chunk, start: chunk[0].start, end: chunk[chunk.length - 1].end });
  }
  // тянем конец каждой реплики до начала следующей — чтобы текст не «мигал» в паузах
  for (let i = 0; i < cues.length - 1; i++) {
    cues[i].end = Math.max(cues[i].end, cues[i + 1].start - 0.02);
  }
  return cues;
}

export const ShortReel: React.FC<ShortReelProps> = ({ clipSrc, title, accentWord, words, durationSec }) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();
  const t = frame / fps;

  const enter = spring({ frame, fps, config: { damping: 200 } });
  const logoY = interpolate(enter, [0, 1], [-24, 0]);
  const logoOpacity = interpolate(frame, [0, 18], [0, 1], { extrapolateRight: "clamp" });
  const titleOpacity = interpolate(frame, [6, 26], [0, 1], { extrapolateRight: "clamp" });

  // --- раскладка (1080×1920) ---
  const sideMargin = Math.round(width * 0.066); // ~72
  const blockW = width - sideMargin * 2; // ~936
  const blockH = Math.round(blockW * 4 / 5); // 5:4 — «чуть шире квадрата»
  const blockTop = Math.round((height - blockH) / 2); // видео по вертикали ПО ЦЕНТРУ (Andrey)

  const cues = React.useMemo(() => toCues(words || []), [words]);
  const active = cues.find((c) => t >= c.start && t < c.end) || (t < (cues[0]?.start ?? 0) ? cues[0] : undefined);

  // заголовок: разбиваем по accentWord для золотого акцента
  const renderTitle = () => {
    if (!accentWord || !title.includes(accentWord)) return <span>{title}</span>;
    const parts = title.split(accentWord);
    return (
      <>
        {parts[0]}
        <span style={{ color: GOLD }}>{accentWord}</span>
        {parts.slice(1).join(accentWord)}
      </>
    );
  };

  return (
    <AbsoluteFill style={{ backgroundColor: COLOR.blue, fontFamily }}>
      {/* лёгкая глубина фона */}
      <AbsoluteFill style={{ background: `radial-gradient(circle at 50% 8%, ${COLOR.white}14 0%, transparent 46%)` }} />

      {/* Лого церкви — белым, по центру сверху (Andrey) */}
      <Img
        src={staticFile(LOGO.markAsset)}
        style={{
          position: "absolute",
          top: Math.round(height * 0.038),
          left: "50%",
          transform: `translateX(-50%) translateY(${logoY}px)`,
          width: 132,
          height: 132,
          opacity: logoOpacity,
          filter: LOGO.whiteOnDarkFilter, // → белый знак (крест = синий фон сквозь вырез)
        }}
      />

      {/* Видео-блок 5:4, скруглённые углы, по центру */}
      <div
        style={{
          position: "absolute",
          top: blockTop,
          left: sideMargin,
          width: blockW,
          height: blockH,
          borderRadius: 44,
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(14,74,119,0.45)",
        }}
      >
        <OffthreadVideo src={staticFile(clipSrc)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />

        {/* читаемость титров поверх видео — мягкий нижний градиент */}
        <AbsoluteFill style={{ background: "linear-gradient(to bottom, transparent 62%, rgba(20,28,33,0.72) 100%)", pointerEvents: "none" }} />

        {/* Титры ВНУТРИ видео — 2-3 слова, текущее слово золотом (Andrey) */}
        {active && (
          <div
            style={{
              position: "absolute",
              bottom: Math.round(blockH * 0.06),
              left: 0,
              right: 0,
              textAlign: "center",
              padding: "0 40px",
              fontSize: 46,
              fontWeight: 500, // НЕ болд (Andrey)
              lineHeight: 1.15,
              color: COLOR.white,
              textShadow: "0 3px 12px rgba(0,0,0,0.6)",
              letterSpacing: -0.3,
            }}
          >
            {active.words.map((w, i) => {
              const on = t >= w.start && t < w.end;
              return (
                <span key={i} style={{ color: on ? GOLD : COLOR.white }}>
                  {w.text}
                  {i < active.words.length - 1 ? " " : ""}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Заголовок ПОД видео (Andrey: лого над → заголовок под), акцент золотом */}
      <div
        style={{
          position: "absolute",
          top: blockTop + blockH + Math.round(height * 0.03),
          left: sideMargin,
          width: blockW,
          textAlign: "center",
          opacity: titleOpacity,
          color: COLOR.white,
          fontSize: 64,
          fontWeight: 900,
          lineHeight: 1.12,
          letterSpacing: -0.5,
          textTransform: "uppercase", // заголовок КАПСЛОКОМ (Andrey)
        }}
      >
        {renderTitle()}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
